import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ToastStack, useAuth, useToastStack } from '@repo/common';
import { ReactComponent as SettingIcon } from '@repo/common/assets/icons/20px/icon_20px_setting_outline.svg';
import { ReactComponent as DashboardIcon } from '@repo/common/assets/icons/20px/icon_20px_dashboard_outline.svg';
import { ReactComponent as VisibleIcon } from '@repo/common/assets/icons/20px/icon_20px_visible_outline.svg';
import { ReactComponent as ReportIcon } from '@repo/common/assets/icons/20px/icon_20px_clipboard_outline.svg';
import { ReactComponent as BellIcon } from '@repo/common/assets/icons/20px/icon_20px_alarm_outline.svg';
import { ReactComponent as DownloadIcon } from '@repo/common/assets/icons/20px/icon_20px_download.svg';
import { ReactComponent as ProfileIcon } from '@repo/common/assets/icons/20px/icon_20px_profile_outline.svg';
import { ReactComponent as ChevronIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { cn, initials } from '../model/derive';
import { CountBadge, MenuBadge, StatusChip, menuBadgeLabel } from '../primitives';
import {
  ACKED_STORE,
  inboxKey,
  inboxToastItems,
  loadAcked,
  saveAcked,
  useInboxChime,
} from './InboxToasts';
import useTitleBadge from '../useTitleBadge';
import {
  createOpsProject,
  createOpsTeam,
  endOpsProject,
  getOpsApplicants,
  getOpsProject,
  getOpsProjects,
  saveOpsBrief,
  saveOpsCheckpoints,
  saveOpsProject,
  saveOpsTasks,
  saveOpsTeams,
  startOpsProject,
  getOpsInbox,
  getOpsNotifications,
  getOpsChatUnread,
  getOpsLiveTasks,
  editOpsLiveTask,
  setOpsMemberStatus,
  resendOpsInvite,
} from '../../../api/twoDays/ops.api';
import {
  STATUS_CHIP,
  fromServer,
  isExtended,
  opsReducer,
  rangeLabel,
  tasksToServer,
} from './opsModel';

/** 말풍선 — 아이콘 세트에 채팅 아이콘이 없어 직접 그린다 */
const ChatIcon = ({ className }) => (
  <svg viewBox='0 0 20 20' fill='none' aria-hidden className={className}>
    <path
      d='M10 3.5c-3.9 0-7 2.6-7 5.8 0 1.6.8 3.1 2.1 4.2L4.5 16.5l3.4-1.3c.7.2 1.4.3 2.1.3 3.9 0 7-2.6 7-5.8s-3.1-6.2-7-6.2Z'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinejoin='round'
    />
  </svg>
);

const NAV = [
  ['setup', 'Setup', SettingIcon],
  ['live', 'Live board', DashboardIcon],
  ['leads', 'Lead inbox', VisibleIcon],
  ['notifications', 'Notifications', BellIcon],
  ['messages', 'Messages', ChatIcon],
  ['reports', 'Reports', ReportIcon],
  ['staff', 'Staff', ProfileIcon],
  ['export', 'Export', DownloadIcon],
];

/** 사이드바 아래 내 카드 — 이름(없으면 이메일 앞부분)과 프로필 사진. 사진 저장 방식은 GNB 와 같다 */
const profileOf = (user) => {
  const name =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    user?.nickname ||
    (user?.email ?? '').split('@')[0] ||
    'Admin';
  const url = user?.profileUrl;
  const image =
    url && !url.startsWith('data:image/') && !/^https?:\/\//.test(url)
      ? `data:image/jpeg;base64,${url}`
      : url || null;
  return { name, image };
};

const navItem = ({ isActive }) =>
  cn(
    'relative flex h-9 items-center gap-2.5 rounded-lg px-3 text-[14px] leading-[140%]',
    isActive
      ? 'bg-surface-green10 font-medium text-text-primary'
      : 'text-text-tertiary hover:bg-grayscale-gray01',
  );

/** 상단 프로젝트 선택기 — 프로젝트 목록 + New project */
const ProjectPicker = ({ state, dispatch }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = state.projects.find((p) => p.id === state.currentId);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => !ref.current?.contains(e.target) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const choose = (id) => {
    if (id !== state.currentId && Object.values(state.dirty).some(Boolean)) {
      // eslint-disable-next-line no-alert
      if (!window.confirm('You have unsaved changes. Leave without saving?')) return;
    }
    dispatch({ type: 'select', id });
    setOpen(false);
  };

  return (
    <div className='flex items-center gap-3'>
      <div ref={ref} className='relative'>
        <button
          type='button'
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className='flex h-10 items-center gap-2 rounded-lg border border-green-deep03/20 bg-grayscale-gray00 px-3.5 text-[14px]'
        >
          <span className='text-[13px] text-text-secondary'>Project</span>
          <span className='font-medium text-text-primary'>{current?.name ?? 'New project'}</span>
          {current && (
            <span className='text-[13px] text-text-secondary'>
              {rangeLabel(current.leaderStartAt, current.endAt)}
            </span>
          )}
          <ChevronIcon className='ml-1 h-4 w-4 text-icon-secondary' />
        </button>
        {open && (
          <div className='absolute left-0 top-12 z-30 flex max-h-[calc(100vh-96px)] w-[312px] flex-col rounded-xl border border-green-deep03/[0.08] bg-grayscale-gray00 p-1.5 shadow-4'>
            {/* 프로젝트가 많으면 목록만 스크롤하고 New project 는 아래에 늘 보인다 */}
            <div className='-mr-1 flex min-h-0 flex-col overflow-y-auto overscroll-contain pr-1'>
              {state.projects.map((p) => {
                const selected = p.id === state.currentId;
                return (
                  <button
                    key={p.id}
                    type='button'
                    onClick={() => choose(p.id)}
                    aria-current={selected || undefined}
                    className={cn(
                      'flex w-full items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-left transition-colors',
                      selected ? 'bg-surface-green10' : 'hover:bg-grayscale-gray01',
                    )}
                  >
                    <span className='flex min-w-0 flex-col gap-0.5'>
                      <span className='truncate text-[14px] font-medium text-text-primary'>
                        {p.name}
                      </span>
                      <span className='flex items-center gap-1.5 text-[12px] text-text-secondary'>
                        {rangeLabel(p.leaderStartAt, p.endAt)}
                        {isExtended(p) && (
                          <span className='rounded bg-grayscale-gray02 px-1.5 py-px text-[11px] font-medium text-text-primary'>
                            Extended
                          </span>
                        )}
                      </span>
                    </span>
                    <span className='shrink-0'>
                      <StatusChip chip={STATUS_CHIP[p.status]} />
                    </span>
                  </button>
                );
              })}
            </div>
            {state.projects.length > 0 && (
              <span className='mx-1 my-1.5 h-px shrink-0 bg-green-deep03/[0.08]' />
            )}
            <button
              type='button'
              onClick={() => choose(null)}
              className='flex w-full shrink-0 items-center rounded-lg px-2.5 py-2.5 text-left text-[14px] font-medium text-green-deep03 hover:bg-grayscale-gray01'
            >
              + New project
            </button>
          </div>
        )}
      </div>
      {current && <StatusChip chip={STATUS_CHIP[current.status]} />}
    </div>
  );
};

/**
 * 2days Ops 콘솔 레이아웃 (/2days/ops) — 운영진만 쓴다.
 * 왼쪽 메뉴: Setup · Live board · Lead inbox · Export. 위쪽: 프로젝트 선택기 + 상태.
 * 운영진(ROLE_A)만 연다 — 라우트는 AdminOnlyRoute 로 감싸고, API 도 /two-days/admin/** 로 막혀 있다.
 */
const OpsLayout = () => {
  const { user } = useAuth() ?? {};
  const [state, dispatch] = useReducer(opsReducer, {
    projects: [],
    currentId: null,
    detail: null,
    dirty: {},
  });
  const { pathname } = useLocation();
  const [error, setError] = useState(null);
  const [listLoaded, setListLoaded] = useState(false);
  const project = state.detail && state.detail.id === state.currentId ? state.detail : null;
  // 고른 프로젝트를 불러오는 중 (New project 가 아닌데 아직 상세가 없음)
  const loading = !listLoaded || (state.currentId != null && !project);

  // 목록 — 가장 최근 프로젝트를 먼저 연다
  useEffect(() => {
    getOpsProjects()
      .then((list) => {
        dispatch({ type: 'list', projects: list ?? [] });
        if (list?.[0]) dispatch({ type: 'select', id: list[0].id });
      })
      .catch(() => setError('Couldn’t load projects. Refresh the page.'))
      .finally(() => setListLoaded(true));
  }, []);

  const reload = (id) =>
    getOpsProject(id)
      .then((p) => dispatch({ type: 'set', project: fromServer(p) }))
      .catch(() => setError('Couldn’t load the project. Refresh the page.'));

  useEffect(() => {
    if (state.currentId != null) reload(state.currentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentId]);

  // Lead inbox — 모든 프로젝트·팀이 한 곳에 모인다. 메뉴 배지와 새 인계 토스트도 써서 레이아웃이 들고 있는다
  const navigate = useNavigate();
  const [inbox, setInbox] = useState(null);
  const [acked, setAcked] = useState(loadAcked);
  // Ops 알림 (C1~C4) — 참여자 알림과 분리된 운영진 공통 목록
  const [opsNotes, setOpsNotes] = useState(null);
  // Ops 메신저 — 운영진이 안 읽은 팀원 메시지 수 (모든 프로젝트)
  const [chatUnread, setChatUnread] = useState(0);
  const reloadInbox = useCallback(() => {
    getOpsInbox()
      .then((list) => setInbox(list ?? []))
      .catch(() => {});
    getOpsNotifications()
      .then((list) => setOpsNotes(list ?? []))
      .catch(() => {});
    getOpsChatUnread()
      .then((n) => setChatUnread(Number(n) || 0))
      .catch(() => {});
  }, []);
  useEffect(() => {
    reloadInbox();
    const timer = window.setInterval(reloadInbox, 20_000);
    return () => window.clearInterval(timer);
  }, [reloadInbox]);
  const ackInbox = useCallback((key) => {
    setAcked((prev) => {
      if (prev.has(key)) return prev;
      // 다른 탭에서 확인한 것도 합쳐 저장한다 (덮어써서 지우지 않게)
      const next = new Set([...loadAcked(), ...prev, key]);
      saveAcked(next);
      return next;
    });
  }, []);
  // Ops 를 탭 여러 개로 열었을 때 — 한 탭에서 확인하면 다른 탭의 토스트·알림음도 멈춘다
  // (확인한 탭에는 토스트가 없는데 뒤의 탭에서 소리만 계속 울리던 문제)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === null || e.key === ACKED_STORE) setAcked(loadAcked());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  const pending = (inbox ?? []).filter((i) => i.state === 'submitted');
  const toReview = pending.length;
  const toasts = pending.filter((i) => !acked.has(inboxKey(i)));
  useInboxChime(toasts);
  // 토스트 — 디자인 시스템 Toast 를 오른쪽 아래에 쌓는다 (새 인계 · 오류 · 화면이 띄우는 알림)
  const pushed = useToastStack();
  const unreadNotes = (opsNotes ?? []).filter((n) => !n.readAt).length;
  useTitleBadge(0);

  // 팀원은 초대 링크로 들어오니, 창으로 돌아오면 새로 불러온다 (고치던 내용이 없을 때만)
  useEffect(() => {
    const onFocus = () => {
      if (state.currentId != null && !Object.values(state.dirty).some(Boolean))
        reload(state.currentId);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  });

  /** 저장 — 서버가 돌려준 프로젝트 전체로 화면을 바꾼다. 성공하면 true */
  const run = async (step, call) => {
    try {
      const p = await call();
      dispatch({ type: 'set', project: fromServer(p), step });
      return true;
    } catch (e) {
      setError(e.response?.data?.msg || 'Couldn’t save. Try again.');
      return false;
    }
  };
  const id = state.currentId;
  const api = {
    create: (fields) => run('project', () => createOpsProject(fields)),
    saveProject: (fields) => run('project', () => saveOpsProject(id, fields)),
    saveBrief: (brief) => run('brief', () => saveOpsBrief(id, brief)),
    getApplicants: () => getOpsApplicants(id),
    createTeam: (name) => run(null, () => createOpsTeam(id, name)),
    saveTeams: (teams) =>
      run('teams', () =>
        saveOpsTeams(
          id,
          teams.map((t) => ({
            id: t.id,
            name: t.name,
            // 신청자는 applicationId, 스태프는 staffId 로 보낸다 (스태프는 늘 Staff backup)
            members: t.members.map((m) => ({
              applicationId: m.staff ? null : m.applicationId,
              staffId: m.staff ? m.staffId : null,
              staffBackup: m.staff ? true : !!m.staffBackup,
            })),
            // 예비 후보 순번
            backups: (t.backups ?? []).map((b) => b.applicationId),
          })),
        ),
      ),
    saveTasks: (tasks) => run('tasks', () => saveOpsTasks(id, tasksToServer(tasks))),
    saveCheckpoints: (rows) =>
      run('checkpoints', () =>
        saveOpsCheckpoints(
          id,
          rows.map((r) => ({ id: r.id, scheduledAt: r.scheduledAt })),
        ),
      ),
    // 시작 뒤 — 진행 중 워크보드 태스크와 팀원 이탈 판정 (오류는 부르는 쪽에서 보여준다)
    getLiveTasks: () => getOpsLiveTasks(id),
    editLiveTask: (taskId, body) => editOpsLiveTask(id, taskId, body),
    resendInvite: (memberId) => run(null, () => resendOpsInvite(id, memberId)),
    setMemberStatus: (memberId, status, reason) =>
      run(null, () => setOpsMemberStatus(id, memberId, { status, reason })),
    start: () => run('startEnd', () => startOpsProject(id)),
    end: () => run('startEnd', () => endOpsProject(id)),
  };
  const ctx = {
    state,
    dispatch,
    project,
    loading,
    api,
    inbox,
    reloadInbox,
    ackInbox,
    /** 화면 토스트 — toast({ type, title, message }) */
    toast: pushed.push,
    opsNotes,
    setOpsNotes,
    chatUnread,
    setChatUnread,
  };
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => setMenuOpen(false), [pathname]);

  const me = profileOf(user);
  const sidebar = (
    <>
      <div className='-mx-3 mb-3 flex h-[72px] items-center gap-2.5 border-b border-green-deep03/[0.08] px-4'>
        <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-green-deep02 text-[13px] font-medium text-grayscale-gray00'>
          2d
        </span>
        <span className='flex min-w-0 flex-1 flex-col'>
          <span className='truncate text-[15px] font-medium leading-[140%] text-text-primary'>
            2 days project
          </span>
          <span className='text-[12px] leading-[140%] text-text-secondary'>Ops console</span>
        </span>
        <span className='rounded-full bg-green-deep04 px-2.5 py-0.5 text-[12px] font-medium text-grayscale-gray00'>
          Ops
        </span>
      </div>
      <nav className='flex flex-col gap-0.5' aria-label='Ops console'>
        <span className='flex h-7 items-center px-3 text-[12px] font-medium text-text-secondary'>
          Console
        </span>
        {NAV.map(([path, label, Icon]) => (
          <NavLink key={path} to={`/2days/ops/${path}`} className={navItem}>
            {({ isActive }) => (
              <>
                {isActive && (
                  <span
                    aria-hidden
                    className='absolute -left-3 top-1/2 -mt-2.5 h-5 w-[3px] rounded-r-sm bg-green-deep02'
                  />
                )}
                <Icon className='h-5 w-5 shrink-0 text-icon-secondary' />
                {label}
                {path === 'leads' && <CountBadge count={toReview} className='ml-auto' />}
                {path === 'messages' && <CountBadge count={chatUnread} className='ml-auto' />}
                {path === 'notifications' && (
                  <CountBadge count={unreadNotes} className='ml-auto' />
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>
      <NavLink to='/2days/ops/settings' className={(s) => cn(navItem(s), 'mb-2 mt-auto')}>
        {({ isActive }) => (
          <>
            {isActive && (
              <span
                aria-hidden
                className='absolute -left-3 top-1/2 -mt-2.5 h-5 w-[3px] rounded-r-sm bg-green-deep02'
              />
            )}
            <SettingIcon className='h-5 w-5 shrink-0 text-icon-secondary' />
            Settings
          </>
        )}
      </NavLink>
      {/* 로그인한 운영진 — Ops 콘솔은 관리자만 열 수 있어 역할은 Ops 로 고정 */}
      <div className='flex items-center gap-2.5 rounded-xl border border-green-deep03/[0.08] bg-grayscale-gray00 p-2.5 shadow-4'>
        {me.image ? (
          <img
            src={me.image}
            alt=''
            className='h-[34px] w-[34px] shrink-0 rounded-full object-cover'
          />
        ) : (
          <span className='flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-surface-green10 text-[12px] font-medium text-green-deep04'>
            {initials(me.name)}
          </span>
        )}
        <span className='flex min-w-0 flex-col'>
          <span className='truncate text-[14px] font-medium leading-[140%] text-text-primary'>
            {me.name}
          </span>
          <span className='truncate text-[12px] leading-[140%] text-text-secondary'>Ops</span>
        </span>
      </div>
    </>
  );
  const sidebarClass =
    'w-64 shrink-0 flex-col overflow-y-auto border-r border-green-deep03/[0.12] bg-grayscale-gray00 px-3 pb-3';

  return (
    <div className='flex min-h-screen bg-bg-twodays'>
      <aside className={cn(sidebarClass, 'sticky top-0 hidden h-screen lg:flex')}>{sidebar}</aside>
      {menuOpen && (
        <div className='fixed inset-0 z-[950] lg:hidden'>
          <button
            type='button'
            aria-label='Close menu'
            onClick={() => setMenuOpen(false)}
            className='absolute inset-0 h-full w-full bg-grayscale-gray10/40'
          />
          <aside
            role='dialog'
            aria-modal='true'
            aria-label='Menu'
            className={cn(sidebarClass, 'relative flex h-full max-w-[85vw] shadow-4')}
          >
            {sidebar}
          </aside>
        </div>
      )}

      <div className='flex min-w-0 flex-1 flex-col'>
        <header className='sticky top-0 z-20 flex min-h-[72px] flex-wrap items-center gap-3 border-b border-green-deep03/[0.08] bg-grayscale-gray00 px-4 py-3 sm:px-8 lg:px-10'>
          <button
            type='button'
            onClick={() => setMenuOpen(true)}
            aria-label={menuBadgeLabel(toReview)}
            className='relative -ml-2 h-9 rounded-lg px-2 text-[14px] font-medium text-text-primary hover:bg-grayscale-gray01 lg:hidden'
          >
            Menu
            <MenuBadge count={toReview} />
          </button>
          <ProjectPicker state={state} dispatch={dispatch} />
        </header>
        <main className='w-full max-w-[1600px] flex-1 px-4 py-6 sm:px-8 lg:px-10 lg:py-10'>
          <Outlet context={ctx} />
        </main>
        <ToastStack
          toasts={[
            ...inboxToastItems(toasts, (i) => {
              ackInbox(inboxKey(i));
              navigate(`/2days/ops/leads/${i.id}`);
            }),
            ...pushed.toasts,
            ...(error ? [{ id: 'ops-error', type: 'error', state: 'white', title: error }] : []),
          ]}
          onClose={(id) => {
            if (id === 'ops-error') setError(null);
            else if (id.startsWith('inbox:')) ackInbox(id.slice('inbox:'.length));
            else pushed.close(id);
          }}
        />
      </div>
    </div>
  );
};

export default OpsLayout;
